import { Info } from "@tamagui/lucide-icons";
import { action, computed, makeObservable, reaction } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../activities/types";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import { PlanModelStep } from "../../plans/PlanModel";
import BaseModuleModel, {
  type MenuOption,
  type ModuleModelData,
  type Snapshot,
} from "../core/BaseModuleModel";
import {
  PlanInfoQuickstartModuleUI,
  PlanInfoQuickstartModuleUIBlockUI,
} from "./PlanInfoQuickstartModuleUI";

export type PlanInfoModuleSnapshot = {
  text: string;
};

export interface PlanInfoModuleModelData
  extends ModuleModelData<PlanInfoModuleSnapshot> {
  coverUrl: string;
  backgroundColor: string | null;
  description: string;
  links: { name?: string; url: string }[];
}

export default class PlanInfoModuleModel extends BaseModuleModel<
  PlanInfoModuleSnapshot,
  PlanInfoModuleModelData
> {
  constructor(stores: Stores, api: Api, data: PlanInfoModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      coverUrl: data.coverUrl ?? "",
      backgroundColor: data.backgroundColor ?? null,
      links: data.links ?? [],
      description: data.description ?? "",
    });

    makeObservable(this);

    reaction(
      () => this.data.title,
      async (title) => {
        // Also set plan title to the same
        if (title) {
          await this.plan.setTitle(title);
        }
      },
    );
  }

  // Used to show setting
  hasHelperGuide = false;

  @action async updateSnapshotWithoutSave() {
    return;
  }

  @computed get userList() {
    const plan = this.plan;

    const userListStr = plan.users
      .map((user) => user.data.displayName)
      .join(", ");

    return userListStr;
  }

  @action async setCoverUrl(url: string) {
    this.data.coverUrl = url;
    this.data.backgroundColor = null;
    this.updateTimestampWithoutSave();
    await this.save();

    this.postActivityEvent(
      ActivityEventType.Edited,
      TKey.ActivityPlanInfoImageUpdated,
      {},
    );
    this.sendTelemetryEvent("cover_set");
  }

  @action setBackgroundColor = async (color: string) => {
    if (color !== this.data.backgroundColor) {
      this.data.coverUrl = "";
      this.data.backgroundColor = color;
      await this.save();
    }
  };

  @action setDescription = async (text: string) => {
    if (text !== this.data.description) {
      const oldValue = this.data.description ?? "";
      this.data.description = text;
      this.updateTimestampWithoutSave();
      await this.save();

      this.postActivityEvent(
        ActivityEventType.Edited,
        TKey.ActivityPlanInfoDescriptionUpdated,
        {
          oldValue,
          newValue: this.data.description ?? "",
        },
      );
      this.sendTelemetryEvent("text_updated");

      this.plan.trackProgress(PlanModelStep.hasDescription, true);
    }
  };

  renderMenuOptions(): MenuOption[] {
    return [];
  }

  toFireStore(): Omit<PlanInfoModuleModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      ...this.data,
    };
  }

  render() {
    return <PlanInfoQuickstartModuleUI module={this} />;
  }

  renderStatic() {
    return null;
  }

  renderBlock() {
    return <PlanInfoQuickstartModuleUIBlockUI module={this} />;
  }

  renderStaticBlock() {
    return this.renderBlock();
  }

  renderIcon() {
    return <Info />;
  }

  static is(
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ): module is PlanInfoModuleModel {
    return module instanceof PlanInfoModuleModel;
  }
}
